import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  addSpreadsheetAccess,
  removeSpreadsheetAccess,
  setSpreadsheetCheckingAccess,
  clearAccessCheckTrigger,
  setSpreadsheetAccessChecked,
  clearPendingAuthCheck,
  setPendingColumnOptionsRequest,
  updateRecentlyGrantedAccessRetry,
  clearRecentlyGrantedAccess,
} from '../../lib/redux/sheets/slice';
import { useSendWindowMessages } from './useSendWindowMessages';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';

export function useSpreadsheetAuth() {
  const dispatch = useDispatch();
  const { sendAuthStatus } = useSendWindowMessages();
  const { makeApiCall } = useSpreadsheetApiCall();
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const windowMessages = useSelector((state: RootState) => state.googleSheets.windowMessages);
  const spreadsheetData = useSelector((state: RootState) => state.googleSheets.spreadsheetData);
  
  const currentSpreadsheetId = windowMessages.latestSpreadsheetIdRequested;
  const pendingAuthCheck = windowMessages.pendingAuthCheck;
  const recentlyGrantedAccess = windowMessages.recentlyGrantedAccess;
  const tokenConfig = auth.tokenConfig;
  const triggerAccessCheckForId = spreadsheetData.triggerAccessCheckFor;

  // Track timeouts per spreadsheet for robust cleanup
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout | undefined>>({});

  // Fast retries after auth grant or token availability
  const FAST_RETRY_DELAYS_MS = [2000, 5000, 10000];

  // Combined delays for recently-granted-access checks: quick attempts, then longer gaps to avoid rate limits
  const ACCESS_CHECK_DELAYS_MS = [2000, 5000, 10000, 45000, 45000];

  const clearTimeoutFor = useCallback((spreadsheetId: string) => {
    const t = timeoutRefs.current[spreadsheetId];
    if (t) {
      clearTimeout(t);
      timeoutRefs.current[spreadsheetId] = undefined;
    }
  }, []);

  // Function to check access for recently granted spreadsheet with retry logic
  const checkRecentlyGrantedAccess = useCallback(async (spreadsheetId: string, retryAttempt: number) => {
    const checkId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    if (!tokenConfig) {
      return;
    }

    try {
      dispatch(updateRecentlyGrantedAccessRetry({
        retryAttempt,
        isRetrying: true,
        lastError: undefined
      }));

      const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title`;
      const result = await makeApiCall({ url });

      const hasAccess = result.success;
      
      if (hasAccess) {
        dispatch(addSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, true);
        dispatch(setPendingColumnOptionsRequest({ spreadsheetId }));
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: undefined
        }));
        setTimeout(() => {
          dispatch(clearRecentlyGrantedAccess());
        }, 2000);

      } else if (retryAttempt < ACCESS_CHECK_DELAYS_MS.length) {
        // Retry with next delay
        const delay = ACCESS_CHECK_DELAYS_MS[retryAttempt];
        const errorMsg = `Access check failed (${result.status}). Retrying in ${delay/1000}s...`;

        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: true,
          lastError: errorMsg
        }));

        clearTimeoutFor(spreadsheetId);
        timeoutRefs.current[spreadsheetId] = setTimeout(() => {
          checkRecentlyGrantedAccess(spreadsheetId, retryAttempt + 1);
        }, delay);

      } else {
        // Final failure
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: `Failed to verify access after ${ACCESS_CHECK_DELAYS_MS.length + 1} attempts`
        }));
      }
      
    } catch (error) {
      if (retryAttempt < ACCESS_CHECK_DELAYS_MS.length) {
        // Retry on error
        const delay = ACCESS_CHECK_DELAYS_MS[retryAttempt];
        const errorMsg = `Network error. Retrying in ${delay/1000}s...`;

        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: true,
          lastError: errorMsg
        }));

        clearTimeoutFor(spreadsheetId);
        timeoutRefs.current[spreadsheetId] = setTimeout(() => {
          checkRecentlyGrantedAccess(spreadsheetId, retryAttempt + 1);
        }, delay);

      } else {
        // Final error
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
        dispatch(updateRecentlyGrantedAccessRetry({
          retryAttempt,
          isRetrying: false,
          lastError: `Network error after ${ACCESS_CHECK_DELAYS_MS.length + 1} attempts`
        }));
      }
    }
  }, [tokenConfig, dispatch, sendAuthStatus, makeApiCall, clearTimeoutFor]);

  // Simple access check function for other use cases
  const checkSpreadsheetAccess = useCallback(async (spreadsheetId: string) => {
    const checkId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    if (!tokenConfig || !spreadsheetId) {
      return;
    }

    try {
      dispatch(setSpreadsheetCheckingAccess({ id: spreadsheetId, checking: true }));

      const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title`;
      const result = await makeApiCall({ url });

      const hasAccess = result.success;

      if (result.status === 401) {
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
      } else if (hasAccess) {
        dispatch(addSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, true);
      } else {
        dispatch(removeSpreadsheetAccess(spreadsheetId));
        sendAuthStatus(spreadsheetId, false);
      }
    } catch (error) {
      dispatch(removeSpreadsheetAccess(spreadsheetId));
      sendAuthStatus(spreadsheetId, false);
    } finally {
      dispatch(setSpreadsheetCheckingAccess({ id: spreadsheetId, checking: false }));
      dispatch(setSpreadsheetAccessChecked({ id: spreadsheetId, checked: true }));
    }
  }, [tokenConfig, dispatch, sendAuthStatus, makeApiCall]);

  // Watch for recently granted access and start retry flow
  useEffect(() => {
    if (recentlyGrantedAccess && recentlyGrantedAccess.isRetrying && recentlyGrantedAccess.retryAttempt === 0) {
      // Start the retry process
      checkRecentlyGrantedAccess(recentlyGrantedAccess.spreadsheetId, 0);
    }
  }, [recentlyGrantedAccess]);

  // Handle pending auth check requests (for other flows)
  useEffect(() => {
    if (!pendingAuthCheck || !tokenConfig) {
      return;
    }

    checkSpreadsheetAccess(pendingAuthCheck.spreadsheetId);
    dispatch(clearPendingAuthCheck());
  }, [pendingAuthCheck, tokenConfig, dispatch]);

  // Listen for trigger access check requests (for other flows)
  useEffect(() => {
    if (triggerAccessCheckForId && tokenConfig) {
      checkSpreadsheetAccess(triggerAccessCheckForId);
      dispatch(clearAccessCheckTrigger());
    }
  }, [triggerAccessCheckForId, tokenConfig, dispatch]);

  // Auto-check access for current spreadsheet (for other flows)
  useEffect(() => {
    if (!currentSpreadsheetId || !tokenConfig) return;

    const hasAccess = spreadsheetData.spreadsheetAccess.includes(currentSpreadsheetId);
    const isCheckingAccess = spreadsheetData.spreadsheetIdToCheckingAccess[currentSpreadsheetId];
    const hasBeenChecked = spreadsheetData.spreadsheetIdToAccessChecked[currentSpreadsheetId];


    // Only check if we don't have access, aren't currently checking, and haven't been checked yet
    // And we're not in the recently granted access flow
    if (!hasAccess && !isCheckingAccess && !hasBeenChecked && !recentlyGrantedAccess) {
      checkSpreadsheetAccess(currentSpreadsheetId);
    }
  }, [currentSpreadsheetId, tokenConfig, spreadsheetData.spreadsheetAccess, spreadsheetData.spreadsheetIdToCheckingAccess, spreadsheetData.spreadsheetIdToAccessChecked, recentlyGrantedAccess]);

  // Fast follow-up checks after token becomes available or on refresh
  useEffect(() => {
    if (!tokenConfig || !currentSpreadsheetId) return;

    // Skip if we already have access or are in the recently-granted flow
    const hasAccess = spreadsheetData.spreadsheetAccess.includes(currentSpreadsheetId);
    const isCheckingAccess = spreadsheetData.spreadsheetIdToCheckingAccess[currentSpreadsheetId];
    if (hasAccess || isCheckingAccess) {
      clearTimeoutFor(currentSpreadsheetId);
      return;
    }
    if (recentlyGrantedAccess && recentlyGrantedAccess.spreadsheetId === currentSpreadsheetId) {
      return;
    }

    // Schedule a short sequence of quick re-checks to handle race conditions after refresh
    let attempt = 0;
    const scheduleNext = () => {
      if (attempt >= FAST_RETRY_DELAYS_MS.length) {
        clearTimeoutFor(currentSpreadsheetId);
        return;
      }
      const delay = FAST_RETRY_DELAYS_MS[attempt++];
      clearTimeoutFor(currentSpreadsheetId);
      timeoutRefs.current[currentSpreadsheetId] = setTimeout(async () => {
        // If we got access in the meantime, stop
        const nowHasAccess = spreadsheetData.spreadsheetAccess.includes(currentSpreadsheetId);
        const nowChecking = spreadsheetData.spreadsheetIdToCheckingAccess[currentSpreadsheetId];
        if (!nowHasAccess && !nowChecking) {
          await checkSpreadsheetAccess(currentSpreadsheetId);
        }
        // Re-evaluate and schedule next if still needed
        const stillNoAccess = !spreadsheetData.spreadsheetAccess.includes(currentSpreadsheetId);
        const stillChecking = spreadsheetData.spreadsheetIdToCheckingAccess[currentSpreadsheetId];
        if (stillNoAccess && !stillChecking) {
          scheduleNext();
        } else {
          clearTimeoutFor(currentSpreadsheetId);
        }
      }, delay);
    };

    scheduleNext();

    return () => {
      clearTimeoutFor(currentSpreadsheetId);
    };
  }, [tokenConfig, currentSpreadsheetId, spreadsheetData.spreadsheetAccess, spreadsheetData.spreadsheetIdToCheckingAccess, recentlyGrantedAccess, checkSpreadsheetAccess, clearTimeoutFor]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.keys(timeoutRefs.current).forEach((key) => {
        const t = timeoutRefs.current[key];
        if (t) clearTimeout(t);
      });
    };
  }, []);
} 